<script lang="ts">
  import { CATEGORIES, CATEGORY_META, PATTERNS, TRACK_POINTS, TRACK_NAMES, ALBUM_MIN_DECADES } from '@banger/shared';
  import PatternGrid from './PatternGrid.svelte';

  const EXAMPLES = ['corners', 'plus', 'x'] as const;

  /** With `onclose` it is a full-screen overlay (Esc / Back closes); without it, the /how-to-play page. */
  let { onclose, backLabel = '← Back' }: { onclose?: () => void; backLabel?: string } = $props();

  const WHAT: Record<(typeof CATEGORIES)[number], string> = {
    title: "Type the song's name. You need the whole title.",
    artist: 'Type the band or singer\'s name. Featured artists count too, and band leaders ("Joan Jett").',
    decade: 'Tap the decade it came out in (60s to 20s).',
    before2000: 'Tap BEFORE if it came out in 1999 or earlier, AFTER if 2000 or later. When only 2000s and later are on, the cutoff becomes 2010.',
    year4: 'Guess the year. Within 4 years counts. Exact year steals!',
  };

  function onkey(e: KeyboardEvent) {
    if (onclose && e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onkey} />

<div class="wrap" class:overlay={!!onclose} role={onclose ? 'dialog' : undefined} aria-modal={onclose ? 'true' : undefined} aria-label="How to play">
  <main>
    <header>
      {#if onclose}
        <button type="button" class="back" onclick={onclose}>{backLabel}</button>
      {:else}
        <a class="back" href="/">← Back</a>
      {/if}
      <h1 class="wordmark">How to play</h1>
    </header>

    <div class="cols">
      <div class="col">
        <section>
          <h2>The goal</h2>
          <p>Get <strong>5 squares in a row</strong> (across, down or diagonal) on your bingo card and shout <strong class="neon-word">BANGER!</strong> The <span class="inline free">★</span> middle square is free.</p>
        </section>

        <section id="modes">
          <h2>Two ways to play</h2>
          <p><strong>Single</strong> (the default): one game, first line wins <strong class="neon-word">BANGER!</strong> Takes about 7 minutes.</p>
          <p><strong>Album</strong>: three tracks on the <em>same card</em>, so your marks carry over. Takes about 25 to 30 minutes. Needs at least {ALBUM_MIN_DECADES} decades turned on.</p>
        </section>

        <section>
          <h2>Each round</h2>
          <ol class="steps">
            <li><b>1</b><span>The disco ball picks a category.</span></li>
            <li><b>2</b><span>A song plays. You have <strong>25 seconds</strong> to answer on your phone and <strong>LOCK IN</strong>.</span></li>
            <li><b>3</b><span>The big screen reveals the song, artist and year.</span></li>
            <li><b>4</b><span>Got it right? Tap one of your squares in that category's color. If you don't tap in time, the game picks your best square.</span></li>
          </ol>
        </section>

        <section>
          <h2>Steals</h2>
          <p>Guess the <strong>exact year</strong> on <span class="tag" style="--c:{CATEGORY_META.year4.color}">{CATEGORY_META.year4.short}</span> and you steal: erase one square from any opponent's card (not the ★, and not one they marked this round).</p>
          <p>Steals happen <em>before</em> lines are checked, so you can block someone's BANGER! Steals work the same in every Album track.</p>
        </section>
      </div>

      <div class="col">
        <section class="legend" aria-label="Category legend">
          <h2>The card legend</h2>
          <ul>
            {#each CATEGORIES as c (c)}
              {@const m = CATEGORY_META[c]}
              <li>
                <span class="sq" style="--c:{m.color}" aria-hidden="true">{m.short}</span>
                <span class="txt"><strong style="color:{m.color}">{m.label}</strong><span>{WHAT[c]}</span></span>
              </li>
            {/each}
            <li>
              <span class="sq free" aria-hidden="true">★</span>
              <span class="txt"><strong>Free square</strong><span>Already yours. It can never be stolen.</span></span>
            </li>
            <li>
              <span class="sq on" style="--c:{CATEGORY_META.title.color}" aria-hidden="true">✓</span>
              <span class="txt"><strong>Marked square</strong><span>Filled in with a check. Five in a row wins.</span></span>
            </li>
          </ul>
        </section>

        <section>
          <h2>Answer tips</h2>
          <ul class="tips">
            <li>Typos are forgiven ("Bohemian Rapsody" counts).</li>
            <li>Capitals and punctuation don't matter ("Guns N Roses").</li>
            <li>You need the whole title, not part of it.</li>
            <li>Abbreviations don't count ("GnR").</li>
            <li>The year is always the <strong>original</strong> release year, not a remaster.</li>
          </ul>
        </section>

        <section>
          <h2>Winning a Single game</h2>
          <p>The first line wins. If two people get a line on the same song, it goes to a <strong>tiebreak</strong>: the first of them to answer the next song correctly wins.</p>
        </section>

        <section id="album">
          <h2>Album mode</h2>
          <table class="tracks">
            <thead><tr><th>Track</th><th>Goal</th><th>Win</th></tr></thead>
            <tbody>
              <tr><td>1 · {TRACK_NAMES[1]}</td><td>First line</td><td>+{TRACK_POINTS[1]}</td></tr>
              <tr><td>2 · {TRACK_NAMES[2]}</td><td>Complete the Remix pattern</td><td>+{TRACK_POINTS[2]}</td></tr>
              <tr><td>3 · {TRACK_NAMES[3]}</td><td>Fill the whole card</td><td>+{TRACK_POINTS[3]}</td></tr>
            </tbody>
          </table>
          <p>You also get <strong>+1 point for every correct answer</strong> in every track. Most total points wins the album. PLATINUM is worth the most, but it doesn't guarantee the win.</p>
          <p>In Album, if several players finish a track on the same song, they all get the points (no tiebreak). If total points tie, the PLATINUM winner ranks higher, then most correct answers.</p>
          <p><strong>The Remix:</strong> one random pattern for the whole room, shown before the track and fixed until someone completes it. Examples:</p>
          <div class="patterns">
            {#each EXAMPLES as k (k)}
              <figure>
                <PatternGrid cells={PATTERNS[k].cells} label={PATTERNS[k].label} size="6.5rem" />
                <figcaption>{PATTERNS[k].label}</figcaption>
              </figure>
            {/each}
          </div>
          <p><strong>PLATINUM!</strong> Every square is wild: any correct answer lets you mark <em>any</em> empty square.</p>
        </section>
      </div>
    </div>
  </main>
</div>

<style>
  .wrap { min-height: 100%; text-align: left; }
  .wrap.overlay { position: fixed; inset: 0; z-index: 100; overflow-y: auto; background: radial-gradient(ellipse at 50% 0%, var(--bg-1), var(--bg-0) 70%), var(--bg-0); }
  main { max-width: 640px; margin: 0 auto; padding: 16px 16px 48px; font-size: 1.1rem; line-height: 1.45; }
  header { display: grid; gap: 8px; justify-items: start; margin-bottom: 8px; }
  h1 { margin: 0; font-size: 2.4rem; align-self: center; justify-self: center; text-align: center; }
  .back {
    font: inherit; font-weight: 800; color: var(--cyan); text-decoration: none; cursor: pointer;
    padding: 0.4em 1.1em; border-radius: 999px; background: rgba(125, 249, 255, 0.08); border: 2px solid rgba(125, 249, 255, 0.5);
    display: inline-flex; align-items: center;
  }
  .cols { display: grid; gap: 0; }
  .col { display: grid; gap: 14px; align-content: start; }
  section { background: rgba(10, 6, 24, 0.55); border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 18px; padding: 14px 16px; margin-top: 14px; }
  h2 { margin: 0 0 8px; font-size: 1.35rem; color: var(--cyan); text-shadow: 0 0 12px rgba(125, 249, 255, 0.5); }
  p { margin: 0 0 0.6em; }
  p:last-child { margin-bottom: 0; }
  .neon-word { color: #fff; text-shadow: 0 0 8px var(--pink), 0 0 18px var(--pink); }
  .steps, .legend ul, .tips { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
  .steps li { display: flex; gap: 12px; align-items: flex-start; }
  .steps b { flex: none; width: 2em; height: 2em; border-radius: 50%; display: grid; place-items: center; background: var(--pink); color: #0a0618; font-weight: 900; box-shadow: 0 0 12px rgba(255, 61, 242, 0.6); }
  .tips li { padding-left: 1.3em; position: relative; }
  .tips li::before { content: '♪'; position: absolute; left: 0; color: var(--pink); }
  .legend li { display: flex; gap: 14px; align-items: center; }
  .txt { display: grid; gap: 2px; }
  .txt > span { opacity: 0.9; }
  .sq {
    flex: none; width: 3.4rem; aspect-ratio: 1; border-radius: 10px; border: 2.5px solid var(--c); color: var(--c);
    background: rgba(10, 6, 24, 0.55); font-weight: 900; font-size: 1rem; display: flex; align-items: center; justify-content: center;
  }
  .sq.on { background: var(--c); color: #0a0618; box-shadow: 0 0 12px var(--c), 0 0 26px color-mix(in srgb, var(--c) 45%, transparent); font-size: 1.4rem; }
  .sq.free, .inline.free { background: #fff; border-color: #fff; color: #0a0618; box-shadow: 0 0 12px rgba(255, 255, 255, 0.6); }
  .inline.free { display: inline-block; padding: 0 0.4em; border-radius: 6px; font-weight: 900; }
  .tracks { width: 100%; border-collapse: collapse; margin-bottom: 0.7em; }
  .tracks th, .tracks td { text-align: left; padding: 0.35em 0.5em; border-bottom: 1px solid rgba(255, 255, 255, 0.14); }
  .tracks th { color: var(--cyan); }
  .patterns { display: flex; flex-wrap: wrap; gap: 14px; justify-content: space-around; margin-bottom: 0.7em; }
  figure { margin: 0; display: grid; gap: 6px; justify-items: center; }
  figcaption { font-weight: 800; }
  .tag { display: inline-block; padding: 0 0.45em; border-radius: 6px; border: 2px solid var(--c); color: var(--c); font-weight: 900; }

  @media (min-width: 1100px) {
    main { max-width: 1760px; padding: 2.2vw 4vw 4vw; font-size: clamp(1.4rem, 1.55vw, 1.9rem); }
    header { grid-template-columns: 1fr auto 1fr; align-items: center; justify-items: start; }
    header h1 { grid-column: 2; font-size: clamp(3rem, 4.4vw, 4.8rem); }
    .cols { grid-template-columns: 1fr 1fr; gap: 2.5vw; }
    section { padding: 1.2vw 1.6vw; border-radius: 22px; }
    h2 { font-size: 1.25em; margin-bottom: 0.4em; }
    .legend li { gap: 1.2vw; }
    .sq { font-size: 1.15rem; width: 3.8rem; }
    .sq.on { font-size: 1.7rem; }
    .back { font-size: 1.2rem; }
  }
</style>
